import _isEmpty from 'underscore/cjs/isEmpty.js'
import { prependHostIfNeeded } from '~/utils/helpers'

/*
 | Every Trustpilot call now goes through our own Nitro routes (server/api/trustpilot/*).
 |
 | Previously fetchReviews/fetchReviewById called the Trustpilot API DIRECTLY from the
 | browser via app/services/trustpilot.js. That put our API key in public JS and made
 | upstream call volume scale with VISITORS — the single largest contributor to being
 | rate-limited, and the one term no server-side cache could ever bound.
 |
 | tests/tests/security/trustpilot-client-bundle.test.ts fails if the upstream host or
 | the API key reappears anywhere under app/ — including in a comment, hence the wording.
 |
 | See agent-os/specs/2026-08-12-1424-trustpilot-rate-limit/.
 */

/*
 | Shared explicit useFetch key.
 |
 | Without it Nuxt derives the key from the CALL SITE, so app/app.vue and this action
 | produced two distinct useAsyncData entries that never deduped — one SSR render could
 | hit /api/trustpilot/data twice. Any new caller must reuse this key.
 */
const TRUSTPILOT_DATA_KEY = 'trustpilot-data'

/*
 | How long a failed summary fetch suppresses retries within one client session.
 | Without a cooldown, trustpilotDataHasError sticking would leave a user whose first
 | page load coincided with an outage widget-less for their whole SPA session.
 */
const ERROR_COOLDOWN_MS = 5 * 60 * 1000

/** Our own route is an in-process cache read; 2s is generous even for a cold miss. */
const REQUEST_TIMEOUT_MS = 2000

export const useTrustpilotStore = defineStore('trustpilot', {
  state: () => ({
    trustpilotData: null,
    trustpilotDataFetchedAt: null,
    trustpilotReviews: null,
    trustpilotSingleReview: undefined,
    isLoadingTrustpilotReviews: false,
    trustpilotDataHasError: false,
    trustpilotDataErrorAt: null,
    trustpilotReviewsHasError: false
  }),

  actions: {
    /*
     | Populates trustpilotData. Called by app/app.vue on every render and by the four
     | review widgets when they find the store empty.
     |
     | MUST be called from a component setup() — useFetch needs Nuxt context. Calling it
     | from onMounted or an event handler would warn and lose the shared-key dedupe.
     */
    async fetchTrustpilotData () {
      if (this.trustpilotData) return

      // Guards the amplification spiral: during an outage every widget would otherwise
      // re-request on every render, multiplying load against the limit that caused it.
      if (
        this.trustpilotDataHasError &&
        Date.now() - (this.trustpilotDataErrorAt ?? 0) < ERROR_COOLDOWN_MS
      ) return

      const { data, error } = await useFetch(prependHostIfNeeded('/api/trustpilot/data'), {
        key: TRUSTPILOT_DATA_KEY,
        timeout: REQUEST_TIMEOUT_MS,
        retry: 0
      })

      if (error.value || !data.value) {
        /*
         | THE BUG THIS FIXES: trustpilotDataHasError was declared but never set, so the
         | widgets' `!trustpilotData && !trustpilotDataHasError` guard stayed open
         | forever and each one re-fetched on every render during an outage.
         */
        this.trustpilotDataHasError = true
        this.trustpilotDataErrorAt = Date.now()
        return
      }

      this.trustpilotData = data.value
      this.trustpilotDataFetchedAt = data.value.fetchedAt ?? null
      this.trustpilotDataHasError = false
      this.trustpilotDataErrorAt = null
    },

    async fetchReviews (params) {
      this.isLoadingTrustpilotReviews = true

      const query = params ?? {}
      const key = `trustpilot-reviews:${query.stars ?? ''}|${query.tagValue ?? ''}`

      const { data, error } = await useFetch(prependHostIfNeeded('/api/trustpilot/reviews'), {
        key,
        query,
        timeout: REQUEST_TIMEOUT_MS,
        retry: 0
      })

      if (error.value || !data.value?.reviews) {
        this.trustpilotReviewsHasError = true
      } else {
        // showReadmore/collapse are view state, so they stay here rather than travelling
        // over the wire. The rest of the mapping now happens server-side.
        this.trustpilotReviews = data.value.reviews.map(review => ({
          ...review,
          showReadmore: false,
          collapse: false
        }))
        this.trustpilotReviewsHasError = false
      }

      this.isLoadingTrustpilotReviews = false
    },

    async fetchReviewById (params) {
      const { data, error } = await useFetch(
        prependHostIfNeeded(`/api/trustpilot/reviews/${params.id}`),
        {
          key: `trustpilot-review:${params.id}`,
          timeout: REQUEST_TIMEOUT_MS,
          retry: 0
        }
      )

      // Was `console.log(error)`, which nuxt/logging.md bans. A missing single review is
      // not worth surfacing — the widgets already degrade when it stays undefined.
      if (error.value || _isEmpty(data.value) || data.value.id !== params.id) return

      this.trustpilotSingleReview = {
        ...data.value,
        showReadmore: false,
        collapse: false
      }
    }
  },

  getters: {
    latestReview () {
      if (_isEmpty(this.trustpilotReviews)) {
        return []
      }
      const list = JSON.parse(JSON.stringify(this.trustpilotReviews))
      /*
       | NOTE: sorts on `a.date`, which does not exist on these objects (the field is
       | `createdAt`), so the comparator always returns NaN and this is a no-op that
       | returns list[0]. Pre-existing; fixing it changes which review renders on every
       | SplitReview/CompactReview page, so it is tracked as a follow-up in the spec.
       */
      return list.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date)
      })[0]
    }
  }
})
